interface PerformanceWidgetScope extends angular.IScope {
  clinic: any;
  hasAppointments: boolean;
  isVisible: boolean;
  loading: boolean;
  getVisibleMetrics: () => PerformanceWidgetMetric[];
  goToPerformanceDashboard: () => void;
};

interface PerformanceWidgetMetric {
  label: string;
  iconPath: string;
  tooltip: string | null;
  value: string | null;
  rawValue: number | null;
  isVisible: boolean;
  unit?: string;
  getValue: (performanceWidgetData: PerformanceWidgetData) => number;
  formatValue: (value: number) => string;
  shouldDisplay: (params: { performanceWidgetData: PerformanceWidgetData; }) => boolean;
  getUnit?: (value: number) => string;
}

interface PerformanceWidgetData {
  lifetimeProductionPerPatient: number;
  roi: number;
  totalProfit: number;
  roiAccuracy: number;
  averageResponseTime: number;
}

angular.module('ClinicApp').directive<PerformanceWidgetScope>(
  'performanceWidget',
  (
    ClinicService,
    OCLogUtil,
    $state,
  ) => ({
    restrict: 'E',
    templateUrl: '/views/directives/performance-widget.html',
    scope: {
      clinic: '=',
      hasAppointments: '=',
    },
    link: ($scope: PerformanceWidgetScope) => {
      const ONE_HOUR_IN_MINUTES = 60;

      const SUPPORTED_PMS = [
        'Dentrix',
        'Eaglesoft',
        'OpenDental',
        'ABELDent',
        'Tracker',
        'ClearDent',
        'Curve',
      ];

      const checkMeetsThreshold = (value: number, threshold: number) =>
        typeof value === 'number' && value >= threshold;

      const ROI_VISIBILITY_AMOUNT_THRESHOLD = 1;
      const TOTAL_PROFIT_VISIBILITY_AMOUNT_THRESHOLD = 1500;
      const ROI_ACCURACY_VISIBILITY_THRESHOLD = 0.98;

      $scope.isVisible = false;
      $scope.loading = true;

      const metrics: PerformanceWidgetMetric[] = [
        {
          label: 'Lifetime Production',
          iconPath: '/img/performance-widget/roi-icon.svg',
          tooltip: null,
          value: null,
          rawValue: null,
          isVisible: false,
          getValue: (performanceWidgetData) => performanceWidgetData.lifetimeProductionPerPatient,
          formatValue: (value) => {
            const roiWithCommas = value.toLocaleString();
            const roiWithoutDecimals = roiWithCommas.split('.')[0];
            return `$${roiWithoutDecimals}`;
          },
          shouldDisplay: ({ performanceWidgetData }) => {
            if (!$scope.clinic.metadata?.opencareProFeature || !$scope.clinic.metadata?.roiDashboardFeature) {
              return false;
            }

            // Check if the clinic has a supported PMS
            const pmsName = $scope.clinic.metadata?.practiceManagementSoftware?.name;
            const isSupportedPms = SUPPORTED_PMS.includes(pmsName);

            if (!isSupportedPms) {
              return false;
            }

            const doesRoiMeetThreshold = checkMeetsThreshold(
              performanceWidgetData.roi,
              ROI_VISIBILITY_AMOUNT_THRESHOLD,
            );

            const doesTotalProfitMeetThreshold = checkMeetsThreshold(
              performanceWidgetData.totalProfit,
              TOTAL_PROFIT_VISIBILITY_AMOUNT_THRESHOLD,
            );

            const doesRoiAccuracyMeetThreshold = checkMeetsThreshold(
              performanceWidgetData.roiAccuracy,
              ROI_ACCURACY_VISIBILITY_THRESHOLD,
            );

            return doesRoiMeetThreshold && doesTotalProfitMeetThreshold && doesRoiAccuracyMeetThreshold;
          },
        },
        {
          label: 'Overall Rating',
          tooltip: 'Calculated based on all time requests',
          iconPath: '/img/performance-widget/rating-icon.svg',
          value: null,
          rawValue: null,
          isVisible: false,
          getValue: () => $scope.clinic.aggregateReviewData?.averageRating,
          formatValue: (value) => value.toFixed(1),
          shouldDisplay: () => true,
        },
        {
          label: 'Response Time',
          tooltip: 'Calculated based on last 10 requests',
          iconPath: '/img/performance-widget/response-time-icon.svg',
          getUnit: (value) => {
            const numMinutes = Math.floor(value);
            if (numMinutes > ONE_HOUR_IN_MINUTES) {
              return 'hrs';
            }
            return 'min';
          },
          value: null,
          rawValue: null,
          isVisible: false,
          getValue: (performanceWidgetData) => performanceWidgetData.averageResponseTime * ONE_HOUR_IN_MINUTES,
          formatValue: (value) => {
            const numMinutes = Math.floor(value);
            if (numMinutes > ONE_HOUR_IN_MINUTES) {
              const numHours = numMinutes / ONE_HOUR_IN_MINUTES;
              return numHours.toFixed(1);
            }

            return numMinutes.toFixed(0);
          },
          shouldDisplay: () => true,
        },
      ];

      $scope.getVisibleMetrics = () => metrics.filter((metric) => metric.value !== null && metric.isVisible);
      $scope.goToPerformanceDashboard = () => {
        $state.go('clinic.performance', {
          id: $scope.clinic.id,
          tab: 'overview',
        });
      };

      const loadMetrics = async () => {
        try {
          const performanceWidgetData = await ClinicService.getPerformanceWidgetData($scope.clinic.id);

          metrics.forEach((metric) => {
            const value = metric.getValue(performanceWidgetData);
            metric.rawValue = value;

            // Handle cases where the API returns null values
            if (!value) {
              metric.value = null;
              metric.isVisible = false;
              return;
            }

            metric.value = metric.formatValue(metric.rawValue);
            metric.unit = metric.getUnit?.(metric.rawValue);
            metric.isVisible = metric.shouldDisplay({ performanceWidgetData });
          });

          const areAllMetricsHidden = metrics.every((metric) => !metric.isVisible);
          setVisibility(areAllMetricsHidden ? false : null);
        } catch (error) {
          OCLogUtil.rollbar.error('Error loading performance metrics', error);
        } finally {
          $scope.loading = false;
          setVisibility(true);
          $scope.$apply();
        }
      };

      const isClinicEligibleForPerformanceWidget = () => $scope.hasAppointments;

      const setVisibility = (forcedVisibility: boolean | null = null) => {
        const isEligible = typeof forcedVisibility === 'boolean'
          ? forcedVisibility
          : isClinicEligibleForPerformanceWidget();

        if (isEligible !== $scope.isVisible) {
          $scope.$emit('performance-widget-visibility-changed', isEligible);
          $scope.isVisible = isEligible;
        }
      };

      loadMetrics();
      setVisibility();
    },
  }),
);
